import { yupResolver } from '@hookform/resolvers/yup';
import { differenceInYears, isValid } from 'date-fns';
import * as Yup from 'yup';

import { OptionType } from '@/components/inputs/types';
import { i18n } from '@/utils/i18n';

export enum CompanyOwnershipTypeEnum {
  NPO = 'Non-Profit',
  LLC = 'LLC',
  CORPORATION = 'Corporation',
  GOVERMENT = 'Government',
  PARTNERSHIP = 'Partnership',
  SOLE_PROPIETORSHIP = 'Sole Proprietorship'
}

export enum OrganizationCategoryTypeEnum {
  CHARITABLE = '8398',
  RELIGIOUS = '8661',
  EDUCATIONAL = '8299'
}

export const organizationCategoriesOptions: OptionType[] = [
  { label: 'Charitable and Social Service Org- Fundraising ', value: OrganizationCategoryTypeEnum.CHARITABLE },
  { label: 'Religious Org', value: OrganizationCategoryTypeEnum.RELIGIOUS },
  { label: 'Schools and educational services', value: OrganizationCategoryTypeEnum.EDUCATIONAL }
];

const MIN_AGE = 18;

// Schemas
// General
const generalSchema = Yup.object().shape({
  companyLegalName: Yup.string()
    .required(i18n.t('npoPaymentOnboarding.general_form.companyLegalName_required'))
    .max(50, i18n.t('mix.max_length', { LENGTH: '50' })),
  companyDbaName: Yup.string()
    .required(i18n.t('npoPaymentOnboarding.general_form.companyDbaName_required'))
    .max(50, i18n.t('mix.max_length', { LENGTH: '50' })),
  companyOwnershipType: Yup.string()
    .required(i18n.t('npoPaymentOnboarding.general_form.companyOwnershipType_required'))
    .oneOf(
      Object.values(CompanyOwnershipTypeEnum),
      i18n.t('npoPaymentOnboarding.general_form.companyOwnershipType_valid_type')
    ),
  organizationCategory: Yup.string()
    .required(i18n.t('npoPaymentOnboarding.general_form.organizationCategory_required'))
    .oneOf(
      Object.values(OrganizationCategoryTypeEnum),
      i18n.t('npoPaymentOnboarding.general_form.organizationCategory_valid_type')
    ),
  companyFederalTaxId: Yup.string()
    .required(i18n.t('npoPaymentOnboarding.general_form.companyFederalTaxId_required'))
    .length(9, i18n.t('mix.exact_length', { LENGTH: 9 }))
    .matches(/^\d+$/, i18n.t('npoPaymentOnboarding.general_form.companyFederalTaxId_only_numbers')),
  companyUrl: Yup.string().required(i18n.t('npoPaymentOnboarding.general_form.companyUrl_required')),
  // companyMcc: Yup.string().length(4, i18n.t('mix.exact_length', { LENGTH: 4 })),
  customerSupportNumber: Yup.string().required(
    i18n.t('npoPaymentOnboarding.general_form.customerSupportNumber_required')
  ),
  companyPhoneNumber: Yup.string().required(i18n.t('npoPaymentOnboarding.general_form.companyPhoneNumber_required')),
  legalAddress: Yup.string().required(i18n.t('npoPaymentOnboarding.general_form.legalAddress_required')),
  dbaAddress: Yup.string().required(i18n.t('npoPaymentOnboarding.general_form.dbaAddress_required'))
});

// Owner
const ownerSchema = Yup.object().shape({
  firstName: Yup.string()
    .required(i18n.t('npoPaymentOnboarding.owner_form.firstName_required'))
    .max(40, i18n.t('mix.max_length', { LENGTH: '40' })),
  middleName: Yup.string().max(40, i18n.t('mix.max_length', { LENGTH: '40' })),
  lastName: Yup.string()
    .required(i18n.t('npoPaymentOnboarding.owner_form.lastName_required'))
    .max(50, i18n.t('mix.max_length', { LENGTH: '50' })),
  emailAddress: Yup.string()
    .email(i18n.t('mix.valid_email'))
    .test('no-plus-sign', i18n.t('npoPaymentOnboarding.owner_form.emailAddress_no_plus'), (value) => {
      if (!value) return true;
      const [localPart] = value.split('@');
      return !localPart.includes('+');
    })
    .required(i18n.t('npoPaymentOnboarding.owner_form.emailAddress_required')),
  phone: Yup.string().required(i18n.t('npoPaymentOnboarding.owner_form.phone_required')),
  dateOfBirth: Yup.string()
    .required(i18n.t('npoPaymentOnboarding.owner_form.dateOfBirth_required'))
    .test('is-18-or-older', i18n.t('npoPaymentOnboarding.owner_form.dateOfBirth_invalid_age'), (value) => {
      if (!value) return false;
      const parsedDate = new Date(value);
      return isValid(parsedDate) && differenceInYears(new Date(), parsedDate) >= MIN_AGE;
    }),
  socialSecurityNumber: Yup.string()
    .required(i18n.t('npoPaymentOnboarding.owner_form.socialSecurityNumber_required'))
    .matches(/^\d+$/, i18n.t('payment_methods.account_only_digits'))
    .length(9, i18n.t('mix.exact_length', { LENGTH: 9 })),
  driversLicenseNumber: Yup.string()
    .required(i18n.t('npoPaymentOnboarding.owner_form.driversLicenseNumber_required'))
    .max(20, i18n.t('mix.max_length', { LENGTH: '20' })),
  address: Yup.string().required(i18n.t('npoPaymentOnboarding.owner_form.address_required')),
  signingManager: Yup.boolean()
    .oneOf([true], i18n.t('npoPaymentOnboarding.owner_form.signingManager_force_true'))
    .required(i18n.t('npoPaymentOnboarding.owner_form.signingManager_required'))
});

// RiskProfile
const riskProfileSchema = Yup.object().shape({
  averageTransactionAmount: Yup.number()
    .typeError(i18n.t('mix.must_be_number'))
    .moreThan(-1, i18n.t('mix.must_be_positive'))
    .required(i18n.t('npoPaymentOnboarding.risk_profile_form.averageTransactionAmount_required')),
  highestTicket: Yup.number()
    .typeError(i18n.t('mix.must_be_number'))
    .moreThan(-1, i18n.t('mix.must_be_positive'))
    .required(i18n.t('npoPaymentOnboarding.risk_profile_form.highestTicket_required')),
  monthlyProcessingVolume: Yup.number()
    .typeError(i18n.t('mix.must_be_number'))
    .moreThan(-1, i18n.t('mix.must_be_positive'))
    .required(i18n.t('npoPaymentOnboarding.risk_profile_form.monthlyProcessingVolume_required')),
  averageCheckAmount: Yup.number()
    .typeError(i18n.t('mix.must_be_number'))
    .moreThan(-1, i18n.t('mix.must_be_positive'))
    .required(i18n.t('npoPaymentOnboarding.risk_profile_form.averageCheckAmount_required')),
  maximumCheckAchAmount: Yup.number()
    .typeError(i18n.t('mix.must_be_number'))
    .moreThan(-1, i18n.t('mix.must_be_positive'))
    .required(i18n.t('npoPaymentOnboarding.risk_profile_form.maximumCheckAchAmount_required')),
  averageNumberOfChecksPerMonth: Yup.number()
    .typeError(i18n.t('mix.must_be_number'))
    .moreThan(-1, i18n.t('mix.must_be_positive'))
    .required(i18n.t('npoPaymentOnboarding.risk_profile_form.averageNumberOfChecksPerMonth_required')),
  monthlyCheckVolume: Yup.number()
    .typeError(i18n.t('mix.must_be_number'))
    .moreThan(-1, i18n.t('mix.must_be_positive'))
    .required(i18n.t('npoPaymentOnboarding.risk_profile_form.monthlyCheckVolume_required')),
  businessStartDate: Yup.string().required(i18n.t('npoPaymentOnboarding.risk_profile_form.businessStartDate_required'))
});

// BankAccount
const bankAccountSchema = Yup.object().shape({
  bankRoutingNumber: Yup.string()
    .required(i18n.t('payment_methods.ach_routing_number_required'))
    .length(9, i18n.t('mix.exact_length', { LENGTH: 9 })),
  bankAccountNumber: Yup.string()
    .required(i18n.t('payment_methods.ach_account_number_required'))
    .matches(/^\d+$/, i18n.t('payment_methods.account_only_digits'))
    .min(4, i18n.t('payment_methods.account_min_length'))
    .max(17, i18n.t('payment_methods.account_max_length'))
});

// Documents
const documentsSchema = Yup.object().shape({
  voidedCheck: Yup.mixed<File | string>().required(i18n.t('npoPaymentOnboarding.documents_form.voidedcheck_required')),
  driverLicense: Yup.mixed<File | string>().required(
    i18n.t('npoPaymentOnboarding.documents_form.driverLicense_required')
  ),
  doc501c3: Yup.mixed<File | string>().required(i18n.t('npoPaymentOnboarding.documents_form.doc501c3_required')),
  articlesOfIncorporation: Yup.mixed<File | string>().nullable(),
  bankStatements: Yup.mixed<File | string>().nullable()
});

// Types
// GeneralData
export type GeneralData = Yup.InferType<typeof generalSchema>;
// Owner
export type OwnerData = Yup.InferType<typeof ownerSchema>;
// RiskProfile
export type RiskProfileData = Yup.InferType<typeof riskProfileSchema>;
// BankAccount
export type BankAccountData = Yup.InferType<typeof bankAccountSchema>;
// Documents
export type DocumentsData = Yup.InferType<typeof documentsSchema>;

// Resolvers
// GeneralData
export const generalResolver = yupResolver(generalSchema);
// Owner
export const ownerResolver = yupResolver(ownerSchema);
// RiskProfile
export const riskProfileResolver = yupResolver(riskProfileSchema);
// BankAccount
export const bankAccountResolver = yupResolver(bankAccountSchema);
// Documents
export const documentsResolver = yupResolver(documentsSchema);
